import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { fieldLabels } from '../../config/fieldLabels';
import './chart-source-modal.css';

/**
 * Source-data viewer for the statistics gauges.
 *
 * Every gauge folds a slice of the afrekeningsborderel payload into segments and
 * needles, and the field keys are opaque numbers. This shows that slice as
 * pretty-printed JSON so the numbers on a gauge can be traced back to the
 * payload they came from.
 *
 * The JSON is rendered verbatim; the fieldLabels name for each numeric key is
 * shown beside the line as display-only annotation, and what the copy button
 * puts on the clipboard is the clean JSON without it.
 */

// The only environments that expose the source-data viewer.
const SOURCE_DATA_ENVIRONMENTS = ['development', 'staging'];

/**
 * Whether the source-data button belongs in this build.
 *
 * Deliberately an allowlist over the raw REACT_APP_ENVIRONMENT rather than
 * API_CONFIG.environment, which defaults to 'development' when the variable is
 * absent - and it IS absent in a production build, since there is no
 * .env.production for config-env.js to copy. Anything unrecognised, undefined
 * included, hides the button.
 *
 * Takes the value as an argument with no default: a default parameter would
 * read an explicit undefined as "not supplied" and fall back to the ambient
 * environment, which is the one case that must fail closed.
 */
export function isSourceDataVisible(env) {
  return SOURCE_DATA_ENVIRONMENTS.indexOf(env) !== -1;
}

/**
 * Copy just the named keys out of a payload object.
 *
 * klassement_totalen carries hundreds of fields, so each gauge names the
 * handful it actually reads rather than dumping the whole object.
 */
export function pickFields(obj, keys) {
  const out = {};
  if (obj) {
    keys.forEach((key) => {
      if (obj[key] !== undefined) {
        out[key] = obj[key];
      }
    });
  }
  return out;
}

/**
 * The source slice as pretty-printed, valid JSON.
 */
export function buildSourceJson(source) {
  return JSON.stringify(source === undefined ? null : source, null, 2);
}

/**
 * Split pretty JSON into lines, tagging each one that opens a numeric field key
 * with that field's human label. Lines with no known label carry null.
 */
export function annotateJsonLines(jsonText) {
  return String(jsonText).split('\n').map((text) => {
    const match = text.match(/^\s*"(\d+)":/);
    const label = match ? fieldLabels[match[1]] : null;
    return { text, label: label || null };
  });
}

function SourceDataButton({ title, source }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Production renders no source-data viewer. REACT_APP_ENVIRONMENT is inlined
  // at build time, so this cannot be flipped at runtime - but it is a render
  // guard, not tree shaking: the dialog code still ships, it just never mounts.
  if (!isSourceDataVisible(process.env.REACT_APP_ENVIRONMENT)) {
    return null;
  }

  const json = buildSourceJson(source);
  const lines = annotateJsonLines(json);

  const toggle = () => {
    setIsOpen((open) => !open);
    setCopied(false);
  };

  const handleCopy = () => {
    // Clipboard access is unavailable in some browsers and in tests; the button
    // must never break the dialog.
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(json);
        setCopied(true);
      }
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <>
      <button
        type='button'
        className='chart-source-button'
        onClick={toggle}
        aria-label={t('statistics.sourceData')}
        title={t('statistics.sourceData')}
      >
        <i className='mdi mdi-code-braces' aria-hidden='true' />
      </button>

      <Modal isOpen={isOpen} toggle={toggle} size='lg' className='chart-source-modal'>
        <ModalHeader toggle={toggle}>
          <i className='mdi mdi-code-braces' aria-hidden='true' />
          &nbsp;
          {`${t('statistics.sourceData')} — ${title}`}
        </ModalHeader>
        <ModalBody>
          <pre className='chart-source-json' data-testid='chart-source-json'>
            {lines.map((line, i) => (
              <div key={i} className='chart-source-line'>
                <span className='chart-source-code'>{line.text}</span>
                {line.label && (
                  <span className='chart-source-label'>{`  ← ${line.label}`}</span>
                )}
              </div>
            ))}
          </pre>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' outline onClick={handleCopy}>
            {copied ? t('statistics.copied') : t('statistics.copyJson')}
          </Button>
          <Button color='primary' onClick={toggle}>
            {t('statistics.close')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

SourceDataButton.propTypes = {
  title: PropTypes.string,
  source: PropTypes.object,
};

SourceDataButton.defaultProps = {
  title: '',
  source: null,
};

export default SourceDataButton;

/**
 * Whether the gauge footers run in diagnostic mode.
 *
 * Same allowlist as the source-data button, so the two appear and disappear
 * together: outside development and staging the footers stay clean.
 */
export function isFooterSourceVisible() {
  return isSourceDataVisible(process.env.REACT_APP_ENVIRONMENT);
}

/**
 * Footer diagnostic state.
 *
 * `available` is whether this build offers the field names at all; `shown` is
 * whether the reader has asked to see them. They start hidden, since most of
 * the time the footer is read for its numbers, not their provenance.
 */
export function useFooterSource() {
  const [open, setOpen] = useState(false);
  const available = isFooterSourceVisible();

  return {
    available,
    shown: available && open,
    toggle: () => setOpen((wasOpen) => !wasOpen),
  };
}

/**
 * The control that reveals the field names, sitting at the foot of the legend.
 * Renders nothing where the diagnostic is not available.
 */
export function FooterSourceToggle({ available, shown, onToggle }) {
  if (!available) {
    return null;
  }

  return (
    <button
      type='button'
      className='statistics-footer-source-toggle'
      aria-expanded={shown}
      onClick={onToggle}
    >
      {shown ? 'velden verbergen' : 'velden tonen'}
    </button>
  );
}

FooterSourceToggle.propTypes = {
  available: PropTypes.bool,
  shown: PropTypes.bool,
  onToggle: PropTypes.func,
};

/**
 * Where a footer row's number came from, shown beside it once revealed.
 *
 * `source` is a klassement_totalen field key ('246'), which is annotated with
 * its fieldLabels name, or a free-form origin for values that are not payload
 * fields at all - the BPG benchmark, say.
 */
export function FieldSource({ source, shown }) {
  if (!source || !shown) {
    return null;
  }

  const label = fieldLabels[source];
  return (
    <span className='statistics-footer-source'>
      {label ? `${source} \u00b7 ${label}` : source}
    </span>
  );
}

FieldSource.propTypes = {
  source: PropTypes.string,
  shown: PropTypes.bool,
};
